import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useState, useMemo, useCallback } from "react";
import { getProductoPublicById, getProductosPublic} from "../../services/public/ProductosPublic";
import { fileURL } from "../../utils/fileURL";
import { formatoFechaSimple } from "../../utils/formatoFecha";
import { renderTextoRico } from "../../utils/textoRico";
import CarruselRelacionados from "../../components/store/CarruselRelacionados";
import "./ProductDetail.css";
import { useCart } from "./hooks/useCart";

/** Formato: cantidad + unidad (abreviatura o nombre desde unidades_peso) */
const formatWeightWithUnit = (peso, unidadAbreviatura, unidadNombre) => {
  const n = Number(peso);
  if (!Number.isFinite(n)) return "—";
  const unit = (unidadAbreviatura && String(unidadAbreviatura).trim()) || (unidadNombre && String(unidadNombre).trim()) || "";
  return unit ? `${n.toFixed(2)} ${unit}` : n.toFixed(2);
};

const getStock = (p) => {
  const candidates = [
    p?.cantidad_disponible,
    p?.stock,
    p?.cantidad,
    p?.cantidad_stock,
    p?.qty,
    p?.qty_disponible,
    p?.cantidad_disponible_total,
  ];

  for (const v of candidates) {
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return 0;
};

const getCategoryId = (p) =>
  p?.categoria_producto_id ?? p?.categoria_id ?? p?.categoria;

const toArray = (payload) => {
  if (Array.isArray(payload)) return payload;

  const candidates = [
    payload?.data,
    payload?.rows,
    payload?.items,
    payload?.productos,
    payload?.results,
  ];

  for (const c of candidates) {
    if (Array.isArray(c)) return c;
  }
  return [];
};

function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { add } = useCart();
  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [qty, setQty] = useState(1);
  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState(null);

  // El backend responde 404 para lo que no esta a la venta: sin publicar,
  // inactivo o sin stock. No es una falla, asi que no se ofrece reintentar.
  const [noDisponible, setNoDisponible] = useState(false);
  const [showZoom, setShowZoom] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const formatUSD = useCallback((v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return "$0.00";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
    }).format(n);
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setPageError(null);
    setNoDisponible(false);

    try {
      const p = await getProductoPublicById(id);
      setProduct(p);
      const allRaw = await getProductosPublic();
      const all = toArray(allRaw);
      const catId = getCategoryId(p);
      const currentId = Number(p?.producto_id ?? id);
      const relatedCalculated = all
        .filter((x) => String(getCategoryId(x)) === String(catId))
        .filter((x) => Number(x?.producto_id) !== currentId)
        .slice(0, 12);

      setRelated(relatedCalculated);
    } catch (err) {
      setRelated([]);

      // 404 es la respuesta normal para un producto que ya no se vende, no
      // un error del servidor: no se registra en la consola ni se invita a
      // reintentar, porque reintentar nunca lo va a traer.
      if (err?.status === 404) {
        setNoDisponible(true);
      } else {
        console.error("ProductDetail loadData failed:", err);
        setPageError("We couldn’t load this product. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);
  useEffect(() => {
    const anyOverlayOpen = showZoom || showConfirm || isAdding;
    if (!anyOverlayOpen) return;

    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [showZoom, showConfirm, isAdding]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      setShowZoom(false);
      setShowConfirm(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const coreCharge = useMemo(() => {
    const n = Number(product?.core_charge);
    if (!Number.isFinite(n) || n <= 0) return null;
    return n;
  }, [product?.core_charge]);

  const brandName = useMemo(() => {
    const v = String(product?.marca_nombre ?? "").trim();
    return v.length ? v : null;
  }, [product?.marca_nombre]);

  const categoryName = useMemo(() => {
    const v = String(product?.categoria_nombre ?? "").trim();
    return v.length ? v : null;
  }, [product?.categoria_nombre]);

  const cleanDescription = useMemo(() => {
    const raw = String(product?.descripcion ?? "");
    if (!raw.trim()) return null;

    const normalized = raw
      .replace(/\r\n/g, "\n")
      .replace(/[ \t]+/g, " ")
      .split("\n")
      .map((line) => line.trim())
      .filter((line, idx, arr) => !(line === "" && arr[idx - 1] === ""))
      .join("\n")
      .trim();

    return normalized.length ? normalized : null;
  }, [product?.descripcion]);

  const available = useMemo(() => getStock(product), [product]);
  const outOfStock = !loading && available <= 0;

  // Los agotados tambien se listan: la ficha sirve para consultar specs y
  // el PDF tecnico, que es lo que viene a buscar quien rastrea un part
  // number. Lo unico que no se puede es agregarlos al carrito.
  const relatedProducts = related || [];

  const clampQty = useCallback(
    (value) => {
      let next = Number(value);
      if (!Number.isFinite(next) || next < 1) next = 1;
      if (!outOfStock && available > 0 && next > available) next = available;
      return next;
    },
    [available, outOfStock]
  );

  const handleQtyChange = (value) => setQty(clampQty(value));
  const decQty = () => setQty((q) => clampQty(q - 1));
  const incQty = () => setQty((q) => clampQty(q + 1));

  const handleOpenZoom = () => {
    if (outOfStock) return;
    setShowZoom(true);
  };

  const handleAddToCart = async () => {
    if (outOfStock || isAdding) return;

    try {
      setIsAdding(true);
      await new Promise((res) => setTimeout(res, 450));

      const productForCart = {
        ...product,
        precio_unitario: Number(product?.precio_unitario || 0),
        core_charge: coreCharge ?? 0,
      };

      add(productForCart, qty);
      setShowConfirm(true);
    } finally {
      setIsAdding(false);
    }
  };

  if (loading) {
    return (
      <div className="adc-product-page">
        <button className="adc-back-btn" onClick={() => navigate("/store")}>
          ← Back to Products
        </button>

        <div className="adc-pdp-skeleton">
          <div className="skel skel-img" />
          <div className="skel skel-lines" />
        </div>
      </div>
    );
  }

  if (noDisponible) {
    return (
      <div className="adc-product-page">
        <button className="adc-back-btn" onClick={() => navigate("/store")}>
          ← Back to Products
        </button>

        <div className="adc-pdp-error">
          <h2>This part is not available</h2>
          <p>
            It may be out of stock or no longer listed. Browse the catalog for
            what we have on hand right now.
          </p>
          <button className="adc-add-btn" onClick={() => navigate("/store")}>
            Browse products
          </button>
        </div>
      </div>
    );
  }

  if (pageError || !product) {
    return (
      <div className="adc-product-page">
        <button className="adc-back-btn" onClick={() => navigate("/store")}>
          ← Back to Products
        </button>

        <div className="adc-pdp-error">
          <h2>Oops</h2>
          <p>{pageError || "Product not found."}</p>
          <button className="adc-add-btn" onClick={loadData}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const price = Number(product?.precio_unitario || 0);
  const totalNow = price * qty + (coreCharge ? coreCharge * qty : 0);

  return (
    <div className="adc-product-page">
      {isAdding && (
        <div className="adc-product-overlay" role="status" aria-live="polite">
          <div className="adc-product-spinner"></div>
          <p>Adding product to cart…</p>
        </div>
      )}

      <button className="adc-back-btn" onClick={() => navigate("/store")}>
        ← Back to Products
      </button>

      {/* Cabecera propia: el nombre manda en la pagina y no compite con el
          precio. Antes vivia dentro de la columna de compra. */}
      <div className="adc-pdp-header">
        <h1 className="adc-pdp-title">{product.nombre}</h1>
      </div>

      <div className="adc-product-main">
        {/* 1: la foto */}
        <div className="adc-product-media">
          <div
            className={`adc-product-gallery ${outOfStock ? "adc-gallery-disabled" : ""}`}
            role="button"
            tabIndex={outOfStock ? -1 : 0}
            onClick={handleOpenZoom}
            onKeyDown={(e) => {
              if ((e.key === "Enter" || e.key === " ") && !outOfStock) {
                handleOpenZoom();
              }
            }}
            aria-disabled={outOfStock ? "true" : "false"}
            title={outOfStock ? "Unavailable" : "Click to zoom"}
          >
            <img src={fileURL(product.url_image)} alt={product.nombre} />

            {/* La chapa de stock no se repite aca: ya esta en la cabecera. */}
            {!outOfStock && <span className="adc-zoom-hint">Click to zoom</span>}
          </div>

          {/* Los datos con los que se identifica la pieza, debajo de la
              foto. En lista y no en pastillas: es el mismo tipo de dato que
              Specifications y se lee igual. */}
          {(product?.no_parte ||
            product?.numero_serie ||
            product?.condicion_nombre ||
            product?.certificado_nombre) && (
            <div className="adc-ident-card">
              <span className="adc-panel-titulo">Identification</span>

              <div className="adc-specs">
                {product?.no_parte && (
                  <div className="adc-spec-row">
                    <span className="k">Part number</span>
                    <span className="v">{product.no_parte}</span>
                  </div>
                )}

                {product?.numero_serie && (
                  <div className="adc-spec-row">
                    <span className="k">Serial number</span>
                    <span className="v">{product.numero_serie}</span>
                  </div>
                )}

                {product?.condicion_nombre && (
                  <div className="adc-spec-row">
                    <span className="k">Condition</span>
                    <span className="v">{product.condicion_nombre}</span>
                  </div>
                )}

                {product?.certificado_nombre && (
                  <div className="adc-spec-row">
                    <span className="k">Certificate</span>
                    <span className="v">{product.certificado_nombre}</span>
                  </div>
                )}

                {formatoFechaSimple(product?.fecha_certificado) && (
                  <div className="adc-spec-row">
                    <span className="k">Certificate date</span>
                    <span className="v">
                      {formatoFechaSimple(product.fecha_certificado)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>


        {/* 3: todo lo que hace falta para decidir, en un solo panel. */}
        <div className="adc-product-info">
          <div className="adc-buy-card">
            <div className="adc-price-row">
              <div>
                <div className="adc-price">{formatUSD(price)}</div>
                {coreCharge !== null && (
                  <div className="adc-core">
                    Core Charge: <strong>{formatUSD(coreCharge)}</strong>
                  </div>
                )}
              </div>

              {/* Las unidades y el estado, uno debajo del otro: es la misma
                  informacion, del numero al de un vistazo. */}
              <div className="adc-disponibilidad">
                <span
                  className={`adc-availability-pill ${outOfStock ? "out" : "in"}`}
                >
                  {outOfStock
                    ? "Out of stock"
                    : `${available} unit${available > 1 ? "s" : ""} available`}
                </span>

                <span className={`adc-chip-stock ${outOfStock ? "out" : "in"}`}>
                  <i aria-hidden="true" />
                  {outOfStock ? "Out of stock" : "In stock"}
                </span>
              </div>
            </div>

            {/* Cantidad y stepper en la misma linea: el panel tiene mucho
                que mostrar y el renglon suelto solo gastaba alto. */}
            <div className="adc-panel-bloque">
              <div className="adc-qty-row">
                <span className="adc-label">Quantity</span>

                <div className="adc-stepper">
                <button
                  type="button"
                  className="adc-step"
                  onClick={decQty}
                  disabled={outOfStock || qty <= 1}
                  aria-label="Decrease quantity"
                >
                  −
                </button>

                <input
                  type="number"
                  min="1"
                  max={available}
                  value={qty}
                  disabled={outOfStock}
                  onChange={(e) => handleQtyChange(e.target.value)}
                  aria-label="Quantity"
                />

                  <button
                    type="button"
                    className="adc-step"
                    onClick={incQty}
                    disabled={outOfStock || qty >= available}
                    aria-label="Increase quantity"
                  >
                    +
                  </button>
                </div>
              </div>

              <button
                className="adc-add-btn adc-add-btn-primary"
                disabled={outOfStock}
                onClick={handleAddToCart}
                title={outOfStock ? "Unavailable" : "Add to cart"}
              >
                {outOfStock ? "Out of Stock" : "Add to Cart"}
              </button>

              <div className="adc-mini-total">
                <span>Estimated total</span>
                <strong>{formatUSD(totalNow)}</strong>
              </div>
            </div>

            {/* La ficha tecnica dentro del mismo panel: es parte de decidir
                si esta es la pieza, no un anexo aparte. */}
            {(brandName ||
              categoryName ||
              (product?.peso != null && product?.peso !== "")) && (
              <div className="adc-panel-bloque">
                <span className="adc-panel-titulo">Specifications</span>

                <div className="adc-specs">
              {brandName && (
                <div className="adc-spec-row">
                  <span className="k">Brand</span>
                  <span className="v">{brandName}</span>
                </div>
              )}
              {categoryName && (
                <div className="adc-spec-row">
                  <span className="k">Category</span>
                  <span className="v">{categoryName}</span>
                </div>
              )}
              {/* Serial number, Condition y Certificate no van aca: viven en
                  la tarjeta Identification, debajo de la foto. Antes estaban
                  en los dos lados y se leian dos veces seguidas. */}

              {(product?.peso != null && product?.peso !== "") && (
                <div className="adc-spec-row">
                  <span className="k">Weight</span>
                  <span className="v">
                    {formatWeightWithUnit(
                      product.peso,
                      product.unidad_abreviatura ?? product.unidadAbreviatura,
                      product.unidad_nombre ?? product.unidadNombre
                    )}
                  </span>
                </div>
              )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* La descripcion a todo el ancho, debajo de la foto y de la
          informacion del producto: es texto corrido y gana con el ancho. */}
      {cleanDescription && (
        <div className="adc-product-desc">
          <h3>Description</h3>
          <div className="adc-desc-text">
            {renderTextoRico(cleanDescription)}
          </div>
        </div>
      )}

      {relatedProducts.length > 0 && (
        <div className="adc-related">
          <h3>Related Products</h3>

          <CarruselRelacionados
            productos={relatedProducts}
            onSelect={(p) => navigate(`/store/product/${p.producto_id}`)}
            onVerTodos={
              // Al catalogo ya filtrado por la categoria de esta pieza: son
              // justo los productos que el carrusel estaba mostrando.
              getCategoryId(product)
                ? () => navigate(`/store?category=${getCategoryId(product)}`)
                : undefined
            }
          />
        </div>
      )}

      {showZoom && (
        <div
          className="adc-image-zoom"
          role="dialog"
          aria-modal="true"
          aria-label="Image preview"
          onClick={() => setShowZoom(false)}
        >
          <div className="adc-zoom-card" onClick={(e) => e.stopPropagation()}>
            <button
              className="adc-zoom-close"
              onClick={() => setShowZoom(false)}
              aria-label="Close"
              type="button"
            >
              ✕
            </button>
            <img src={fileURL(product.url_image)} alt={product.nombre} />
          </div>
        </div>
      )}

      {showConfirm && (
        <div
          className="adc-product-modal"
          role="dialog"
          aria-modal="true"
          aria-label="Product added to cart"
          onClick={() => setShowConfirm(false)}
        >
          <div className="adc-product-modal-card" onClick={(e) => e.stopPropagation()}>
            <h3>Product added to cart</h3>
            <p>What would you like to do next?</p>

            <div className="adc-product-modal-actions">
              <button className="secondary" onClick={() => setShowConfirm(false)}>
                Continue shopping
              </button>

              <button className="primary" onClick={() => navigate("/store/cart")}>
                Go to cart
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProductDetail;
